import { gql, useMutation } from '@apollo/client'
import BrowserOutOfDateWarning from 'components/BrowserOutOfDateWarning'
import GenericServerError from 'components/GenericServerError'
import SubmitButton from 'components/SubmitButton'
import TextInput from 'components/TextInput'
import { Form, Formik } from 'formik'
import rollbar from 'helpers/rollbar'
import { toFriendlyKitId } from 'helpers/string'
import { useState } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'
import * as yup from 'yup'
import BulmaMessage from '../Bulma/BulmaMessage'
import KitAlreadyRegistered from './KitAlreadyRegistered'
import KitCompleted from './KitCompleted'
import RegInProgress from './RegInProgress'
import {
  BeginRegistrationMutation,
  BeginRegistrationMutationVariables,
  BeginRegistrationMutation_beginRegistration,
} from './__generated__/BeginRegistrationMutation'

const beginRegistrationMutation = gql`
  mutation BeginRegistrationMutation($kitId: ID!, $force: Boolean) {
    beginRegistration(kitId: $kitId, force: $force) {
      success
      kitId
      sessionId
      sku
      status
      observed
      reason
      regSessionAge
      testPartner {
        id
        consentRequired
        consentText
        registrantNoteHtml
      }
    }
  }
`

const validationSchema = yup.object().shape({
  kitId: yup
    .string()
    .trim()
    .label('Kit ID')
    .matches(/^K?([a-zA-Z0-9][a-zA-Z0-9][a-zA-Z0-9][a-zA-Z0-9]-?[a-zA-Z0-9][a-zA-Z0-9][a-zA-Z0-9][a-zA-Z0-9])$/, {
      excludeEmptyString: true,
      message: 'ID numbers are 8 or 9 letters & numbers. Please check you typed it correctly.',
    })
    .required(),
})

export type KitRegistrationInfo = BeginRegistrationMutation_beginRegistration

interface KitIdFormProps {
  onComplete: (info: KitRegistrationInfo) => void
}

const KitIdForm = ({ onComplete }: KitIdFormProps) => {
  const [kitId] = useQueryParam('kitId', StringParam)

  if (kitId) {
    // We're ignoring this query param for now because of an accidental shipment
    // of kits where the QR code (to /register?kitId={kitId}) was out of sync with
    // the actual kit ID printed on the box. Everyone will have to manually enter their
    // kit ID.
    // Discussion at https://ceruleanhealth.slack.com/archives/G01KAA5P2MP/p1631055699005400
    // To avoid confusion, if someone does arrive here with a kit ID, strip it off the URL
    window.history.replaceState({}, '', '/register')
  }

  const [data, setData] = useState<BeginRegistrationMutation>()
  const [beginRegistration] = useMutation<BeginRegistrationMutation, BeginRegistrationMutationVariables>(
    beginRegistrationMutation,
    {
      onCompleted: (d) => setData(d),
    }
  )

  const initialValues = { kitId: '', __form: '' }

  if (data?.beginRegistration.reason === 'ALREADY_REGISTERED') {
    return (
      <KitAlreadyRegistered
        kitId={toFriendlyKitId(data.beginRegistration.kitId)}
        onRestart={() => {
          window.history.replaceState({}, '', '/register')
          setData(null)
        }}
        sku={data.beginRegistration.sku}
        onContinue={async () => {
          const resp = await beginRegistration({
            variables: {
              kitId: data.beginRegistration.kitId,
              force: true,
            },
          })

          const registrationInfo = resp?.data.beginRegistration

          if (registrationInfo?.success) {
            onComplete(registrationInfo)
          }
        }}
      />
    )
  }

  if (data?.beginRegistration.reason === 'IN_PROGRESS') {
    return (
      <RegInProgress
        kitId={toFriendlyKitId(data.beginRegistration.kitId)}
        regSessionAge={data.beginRegistration.regSessionAge}
        onRestart={() => {
          window.history.replaceState({}, '', '/register')
          setData(null)
        }}
        onContinue={async () => {
          const resp = await beginRegistration({
            variables: {
              kitId: data.beginRegistration.kitId,
              force: true,
            },
          })

          const registrationInfo = resp?.data.beginRegistration

          if (registrationInfo?.success) {
            onComplete(registrationInfo)
          }
        }}
      />
    )
  }

  if (data?.beginRegistration.reason === 'COMPLETED') {
    return (
      <KitCompleted
        onRestart={() => {
          window.history.replaceState({}, '', '/register')
          setData(null)
        }}
      />
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setFieldError }) => {
        try {
          const resp = await beginRegistration({
            variables: {
              kitId: values.kitId.trim(),
            },
          })

          const registrationInfo = resp.data.beginRegistration

          if (!registrationInfo.success && registrationInfo.reason === 'KIT_NOT_FOUND') {
            setFieldError('kitId', 'That kit ID is not in the system, please check you typed it correctly')
            return
          }

          if (registrationInfo.success) {
            onComplete(registrationInfo)
          }
        } catch (err) {
          rollbar.error('Kit ID submit error', err)
          setFieldError('__form', err.message)
          throw err
        }
      }}
    >
      {({ errors, isSubmitting }) => (
        <Form>
          <h1 className="title is-size-4-mobile has-text-midnightBlue">Register your details</h1>

          <div className="mb-3">
            <BulmaMessage color="info">
              If you are registering a <b>video-supervised</b> test, please make sure you've had your test supervised{' '}
              <em>before</em> proceeding. Your order confirmation email has all the instructions.
            </BulmaMessage>
          </div>

          <BrowserOutOfDateWarning />

          <TextInput name="kitId" label="Kit ID / barcode Number" type="text" autoComplete="off" autoFocus />

          {errors.__form && <GenericServerError />}

          <div className="buttons is-right mt-5">
            <SubmitButton className="is-midnightBlue" submitting={isSubmitting}>
              Next step: Registration Details
            </SubmitButton>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default KitIdForm
